import { useInfiniteQuery } from "@tanstack/react-query";
import { GET } from "../base.api";
import generateAPI from "../api.generator.tanstack";
import type {
  GetGuestListParams,
  GetGuestListResponse,
  GuestInsightsResponse,
  GetDeletedGuestsParams,
  GetDeletedGuestsResponse,
  GetAllGuestListResponse,
} from "./guest.types";

/**
 * Guest Query Hooks
 * All GET requests for guest data
 */

/**
 * Fetch guest list with pagination and filters
 * 
 * @example
 * ```tsx
 * const query = useGetGuestList({ query: { page: 1, search: "John" } });
 * const guests = query.data?.payload.guestList ?? [];
 * const total = query.data?.payload.guestCount ?? 0;
 * ```
 */
export const useGetGuestList = generateAPI<
  { query: GetGuestListParams },
  GetGuestListResponse
>("get", "wedding/guest/list");

/**
 * Fetch guest statistics (total, accepted, pending, rejected)
 * 
 * @example
 * ```tsx
 * const query = useGetGuestInsights({});
 * const insights = query.data?.payload;
 * ```
 */
export const useGetGuestInsights = generateAPI<
  {},
  GuestInsightsResponse
>("get", "wedding/insights/guests");

/**
 * Get deleted guests with pagination
 * 
 * @example
 * ```tsx
 * const query = useGetDeletedGuests({ query: { page: 1 } });
 * const deletedGuests = query.data?.payload.deletedGuests ?? [];
 * const deletedCount = query.data?.payload.deletedCount ?? 0;
 * ```
 */
export const useGetDeletedGuests = generateAPI<
  { query: GetDeletedGuestsParams },
  GetDeletedGuestsResponse
>("get", "guest/deleted");

/**
 * Get all guests without pagination
 * 
 * @example
 * ```tsx
 * const query = useGetAllGuestList({});
 * const guests = query.data?.payload.guests ?? [];
 * const guestCount = query.data?.payload.guestCount ?? 0;
 * ```
 */
export const useGetAllGuestList = generateAPI<
  {},
  GetAllGuestListResponse
>("get", "wedding/guest/all-list");

/**
 * Infinite scroll query for guest list
 * Use this for infinite scroll/load more functionality
 * 
 * @example
 * ```tsx
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage,
 *   isLoading
 * } = useInfiniteGuestListQuery({ search: "John" });
 * 
 * // Access all guests across pages
 * const allGuests = data?.pages.flatMap(page => page.guestList) ?? [];
 * const totalCount = data?.pages[0]?.guestCount ?? 0;
 * ```
 */
export const useInfiniteGuestListQuery = (
  filters: Omit<GetGuestListParams, "page"> = {}
) => {
  return useInfiniteQuery({
    queryKey: ["wedding/guest/list", filters],
    queryFn: async ({ pageParam = 1, signal }) => {
      const data = await GET<GetGuestListResponse>(
        {
          ext: "wedding/guest/list",
          query: {
            page: pageParam,
            ...filters,
          } as any,
        },
        signal
      );
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      return data.payload;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.guestList.length,
        0
      );
      // Return next page number if there are more guests to fetch
      return totalFetched < lastPage.guestCount ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
